/**
 * SignInPage — MSG91 OTP sign-in for the customer web portal.
 *
 * MSG91's widget handles the entire phone-input + OTP-verify UX in
 * its OWN modal — we just provide a button to open it. This means:
 *   - No phone input or PIN input controls on our side
 *   - No reCAPTCHA, no test-phone allowlist (real OTPs go to any
 *     Indian number on MSG91's network)
 *   - Identical UX to the mobile app (same SMS sender, same
 *     verification flow)
 *
 * On success, `signInWithMsg91()` resolves once Firebase Auth is
 * signed in. AuthProvider's `onAuthStateChanged` listener picks up
 * the new user and re-renders the app to `/plans`.
 */
import { useState } from 'react';
import {
  Anchor,
  Button,
  Card,
  Center,
  List,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPhone } from '@tabler/icons-react';

import { signInWithMsg91 } from '@/lib/firebase';

export function SignInPage() {
  const [signingIn, setSigningIn] = useState(false);

  const onSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithMsg91();
      // Sign-in succeeded; AuthProvider's onAuthStateChanged will
      // re-render the routed shell. We don't navigate imperatively
      // here — the routed surface decides where to land based on
      // the user's primary org state.
    } catch (err) {
      const msg = (err as Error).message || 'Sign-in failed.';
      notifications.show({
        title: "Couldn't sign in",
        message: msg,
        color: 'red',
        autoClose: 8000,
      });
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <Center mih="100vh" p="md">
      <Card withBorder shadow="sm" p="xl" maw={440} w="100%">
        <Stack gap="lg">
          {/* Brand */}
          <Stack gap={4} align="center">
            <Title order={2}>InteriorIO</Title>
            <Text size="sm" c="dimmed" ta="center">
              Sign in to manage your studio's plan and billing.
            </Text>
          </Stack>

          {/* Sign-in CTA — MSG91 modal handles phone + OTP entirely. */}
          <Stack gap="sm" align="stretch">
            <Button
              size="md"
              onClick={onSignIn}
              loading={signingIn}
              leftSection={<IconPhone size={16} />}
            >
              Sign in with phone OTP
            </Button>
            <Text size="xs" c="dimmed" ta="center">
              Use the same phone number you signed into the InteriorIO
              mobile app with.
            </Text>
          </Stack>

          {/* What signing in unlocks — gives empty-state context */}
          <Stack gap={4}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              What you can do here
            </Text>
            <List
              spacing={4}
              size="sm"
              icon={
                <ThemeIcon color="blue" size={14} radius="xl">
                  <IconCheck size={9} />
                </ThemeIcon>
              }
            >
              <List.Item>View your studio's current plan</List.Item>
              <List.Item>Upgrade or change subscription</List.Item>
              <List.Item>Download GST invoices</List.Item>
              <List.Item>Update payment method</List.Item>
              <List.Item>Cancel auto-renewal</List.Item>
            </List>
          </Stack>

          {/* Footer fine print */}
          <Text size="xs" c="dimmed" ta="center">
            By signing in you agree to our{' '}
            <Anchor href="https://interioros.app/terms.html" target="_blank" size="xs">
              Terms
            </Anchor>{' '}
            and{' '}
            <Anchor
              href="https://interioros.app/privacy-policy.html"
              target="_blank"
              size="xs"
            >
              Privacy Policy
            </Anchor>
            .
          </Text>
        </Stack>
      </Card>
    </Center>
  );
}
